import axios from "axios";
import type { InternalAxiosRequestConfig, AxiosError, AxiosResponse, AxiosInstance } from "axios";
import { QueryClient } from "@tanstack/react-query";
import { HTTP_STATUS } from "@constants/http.status.code";
import { SNACKBAR_SEVERITY } from "@constants/notificationStack";
import { AUTH_ENDPOINTS, CLIENT_HANDLED_ERROR_ENDPOINTS, DEFAULT_TIMEOUT, SERVER_ROUTES } from "@constants/api.routes";
import { clearAuthData, getAuthToken, getUser } from "@utils/auth";
import { store } from "@store/store"; // Do not import store here to avoid circular deps
import { clearUser } from "@store/slices/userSlice";
import { resetNotifications } from "@store/slices/notificationSlice";
import { clearEmbedAlternateChrome } from "@store/slices/externalPortalSlice";
import { clearRegistrationSessionState } from "@utils/clearRegistrationSessionState";
import { SERVER_URL } from "@config/env";
import { showNotification } from "@libs/notistack/notificationStack";
import { getTranslation } from "@utils/translation";
import { isTradeSessionAssessmentBodyError } from "@utils/errorHandler";
import { BROWSER_ROUTES } from "@constants/browser.routes";
import type { ConfigErrorResponse } from "./types/types";
import { USER_TYPES } from "@constants/user.roles";
import { getUserTypeName } from "@utils/facultyAccess";
import { isLoginPendingSetupError } from "@features/Login/utils/loginError.utils";


let isUnauthorizedHandled = false;

export const PORTAL_TYPE = "institute";

const resolveUserTypeHeader = (): string => {
	const storedType = getUserTypeName(getUser(), getAuthToken());
	if (storedType === USER_TYPES.FACULTY) {
		return USER_TYPES.FACULTY;
	}
	return PORTAL_TYPE;
};

const HEADERS = {
	"Content-Type": "application/json",
	Accept: "application/json",
} as const;

export const HEADERS_MULTIPART = {
	"Content-Type": "multipart/form-data",
} as const;

export const baseConfig = {
	baseURL: SERVER_URL,
	timeout: DEFAULT_TIMEOUT,
	headers: HEADERS,
};

// Create axios instances
export const createAxiosInstance = (config: Partial<InternalAxiosRequestConfig> = {}): AxiosInstance => {
	const instance = axios.create({
		...baseConfig,
		...config,
	});

	return instance;
};

// Create API instances
export const api = createAxiosInstance();
export const authApi = createAxiosInstance();

// Function to check if endpoint is an auth endpoint
const isAuthEndpoint = (url: string): boolean => {
	return AUTH_ENDPOINTS.some(endpoint => url?.includes(endpoint));
};

const isClientHandledErrorEndpoint = (url: string): boolean => {
	return CLIENT_HANDLED_ERROR_ENDPOINTS.some(endpoint => url?.includes(endpoint));
};

// Function to handle unauthorized access
const handleUnauthorized = (error: AxiosError) => {
	// Always show the notification for 401 errors
	showNotification({
		message:
			(error?.response?.data as { message: string })?.message ||
			getTranslation("psdaCommon.messages.error.api.unauthorized"),
		severity: SNACKBAR_SEVERITY.ERROR,
	});
	if (isUnauthorizedHandled) {
		return; // Prevent multiple handling
	}

	isUnauthorizedHandled = true;

	// Full session wipe (Redux drafts, React Query cache, auth storage) — same as explicit logout
	store.dispatch(clearUser());
	clearRegistrationSessionState(store.dispatch);
	store.dispatch(resetNotifications());
	store.dispatch(clearEmbedAlternateChrome());
	clearAuthData();
};

export const resetUnauthorizedFlag = () => {
	isUnauthorizedHandled = false;
};

// Request interceptors
const requestInterceptor = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
	// If unauthorized flag is set and this is not an auth endpoint, block the request
	if (isUnauthorizedHandled && !isAuthEndpoint(config?.url || "")) {
		// Return a rejected promise to properly block the request
		return Promise.reject(new Error("Unauthorized - request blocked")) as never;
	}
	const token = getAuthToken();
	if (config?.headers) {
		if (token) {
			config.headers.Authorization = `Bearer ${token}`;
		}
		config.headers["x-user-type"] = resolveUserTypeHeader();
	}
	return config;
};

// Response interceptors
const responseInterceptor = (response: AxiosResponse): AxiosResponse => {
	return response?.data || response;
};

const errorInterceptor = (error: AxiosError): Promise<never> => {
	if (error?.response) {
		const axiosError = error as AxiosError;
		const errorData = axiosError?.response?.data as ConfigErrorResponse;
		const requestUrl = error?.config?.url || "";

		// Inline field validation (duplicate check) — never show global toast
		if (isClientHandledErrorEndpoint(requestUrl)) {
			return Promise.reject(error);
		}

		// Session assessment-body validation — shown inline on CreateSessionModal
		if (isTradeSessionAssessmentBodyError(error)) {
			return Promise.reject(error);
		}

		if (errorData?.statusCode === HTTP_STATUS.UNAUTHORIZED) handleUnauthorized(error);

		if (errorData?.statusCode === HTTP_STATUS.NOT_FOUND) {

			showNotification({
				message: getTranslation("psdaCommon.messages.error.api.nresourceNotFound"),
				severity: SNACKBAR_SEVERITY.ERROR,
			});
		}
		if (errorData?.statusCode === HTTP_STATUS.BAD_REQUEST) {
			// Skip showing notification for auth endpoints - they are handled in mutation handlers
			if (!isAuthEndpoint(error?.config?.url || "")) {
				const isMatchedTrades = (error?.config?.url || "").includes("matched-trades");
				showNotification({
					message:
						(error?.response?.data as { message: string })?.message ||
						getTranslation("psdaCommon.messages.error.api.badRequest"),
					severity: isMatchedTrades ? SNACKBAR_SEVERITY.INFO : SNACKBAR_SEVERITY.ERROR,
				});
			}
		}
		if (errorData?.statusCode === HTTP_STATUS.FORBIDDEN) {
			const isSignInRequest =
				requestUrl.includes(SERVER_ROUTES.AUTH.SIGN_IN) || requestUrl.includes("/auth/v1/sign-in");
			if (isSignInRequest && isLoginPendingSetupError(axiosError)) {
				return Promise.reject(error);
			}

			showNotification({
				message:
					(error?.response?.data as { message: string })?.message ||
					getTranslation("psdaCommon.messages.error.api.forbidden"),
				severity: SNACKBAR_SEVERITY.ERROR,
			});
		}
		if (errorData?.statusCode === HTTP_STATUS.CONFLICT) {
			showNotification({
				message:
					(error?.response?.data as { message: string })?.message ||
					getTranslation("psdaCommon.messages.error.api.genericError"),
				severity: SNACKBAR_SEVERITY.ERROR,
			});
		}
		if (errorData?.statusCode === HTTP_STATUS.INTERNAL_SERVER_ERROR) {
			showNotification({
				message:
					(error?.response?.data as { message: string })?.message ||
					getTranslation("psdaCommon.messages.error.api.internalServerError"),
				severity: SNACKBAR_SEVERITY.ERROR,
			});
		}
		if (errorData?.statusCode === HTTP_STATUS.SERVICE_UNAVAILABLE) {
			showNotification({
				message:
					(error?.response?.data as { message: string })?.message ||
					getTranslation("psdaCommon.messages.error.api.serverUnavailable"),
				severity: SNACKBAR_SEVERITY.ERROR,
			});
			// Use window.location for navigation outside React components
			window.location.href = BROWSER_ROUTES.SERVER_ERROR;
		}
	} else if (error?.request) {
		showNotification({
			message:
				(error?.response?.data as { message: string })?.message ||
				getTranslation("psdaCommon.messages.error.api.noResponse"),
			severity: SNACKBAR_SEVERITY.ERROR,
		});
	}
	else if (error?.code === "ERR_NETWORK") {
		// Network error
		showNotification({
			message:
				(error?.response?.data as { message: string })?.message ||
				getTranslation("psdaCommon.messages.error.api.serverUnavailable"),
			severity: SNACKBAR_SEVERITY.ERROR,
		});
		// Use window.location for navigation outside React components
		window.location.href = BROWSER_ROUTES.SERVER_ERROR;
	}
	else {
		showNotification({

			message:
				(error?.response?.data as { message: string })?.message ||
				getTranslation("psdaCommon.messages.error.api.unknown"),
			severity: SNACKBAR_SEVERITY.ERROR,
		});
	}
	return Promise.reject(error);
};

// Apply interceptors
[api, authApi].forEach(instance => {
	instance.interceptors.request.use(requestInterceptor, error => Promise.reject(error));
	instance.interceptors.response.use(responseInterceptor, errorInterceptor);
});

// React Query integration
export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			retry: 1,
			refetchOnWindowFocus: false,
			// staleTime: 5 * 60 * 1000, // 5 minutes
		},
	},
});